if (!customElements.get("m-recently-viewed")) {
  class MRecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.boundInitByScreenSize = this.initByScreenSize.bind(this);
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
      };
      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = parseInt(this.dataset.productsPerRow, 10) || 4;
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.productsToShow = parseInt(this.dataset.productsToShow, 10) || 4;
      this.recommendationPoolSize = parseInt(this.dataset.recommendationPoolSize, 10) || this.productsToShow;
      this.isCartRecommendations = this.dataset.cartRecommendations === "true";
      this.cartProductIds = new Set(this.parseList(this.dataset.cartProductIds));
      this.cartModels = new Set(this.parseList(this.dataset.cartModels));
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener("matchMobile", this.boundInitByScreenSize);
      document.removeEventListener("unmatchMobile", this.boundInitByScreenSize);
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    async init() {
      try {
        if (this.isCartRecommendations) {
          await this.initCartRecommendations();
        } else {
          await this.initRecentlyViewed();
        }
      } catch (error) {
        console.error("Unable to load product recommendations", error);
      }
    }

    async initRecentlyViewed() {
      const query = this.getQueryString(this.productsToShow);
      if (!query) return;

      const root = await this.fetchSection(`${this.dataset.url}${query}`);
      if (!root || !root.innerHTML.trim()) return;

      this.products = root.querySelectorAll(".m-product-card").length;
      if (!this.products) return;

      this.innerHTML = root.innerHTML;
      this.finishRender();
    }

    async initCartRecommendations() {
      const recentQuery = this.getQueryString(this.recommendationPoolSize);
      const requests = [
        { source: "complementary", url: this.dataset.complementaryUrl },
        { source: "related", url: this.dataset.relatedUrl },
        { source: "recent", url: recentQuery ? `${this.dataset.url}${recentQuery}` : "" },
      ].filter((request) => request.url);

      if (!requests.length) return;

      const results = await Promise.allSettled(
        requests.map(async (request) => ({
          source: request.source,
          root: await this.fetchSection(request.url),
        }))
      );
      const fulfilled = results
        .filter((result) => result.status === "fulfilled" && result.value.root)
        .map((result) => result.value);

      if (!fulfilled.length) return;

      const candidates = new Map();
      fulfilled.forEach(({ source, root }) => {
        root.querySelectorAll("[data-recommendation-item]").forEach((item) => {
          const productId = String(item.dataset.productId || "");
          if (!productId || this.cartProductIds.has(productId)) return;

          const candidate = item.cloneNode(true);
          candidate.dataset.recommendationSource = source;
          const score = this.scoreRecommendation(candidate);
          candidate.dataset.recommendationScore = String(score);

          const existing = candidates.get(productId);
          if (!existing || score > existing.score) {
            candidates.set(productId, { node: candidate, score });
          }
        });
      });

      const selected = [...candidates.values()]
        .sort((left, right) => {
          if (right.score !== left.score) return right.score - left.score;
          return this.getPrice(left.node) - this.getPrice(right.node);
        })
        .slice(0, this.productsToShow)
        .map((candidate) => candidate.node);

      if (!selected.length) return;

      const shell = fulfilled[0].root.cloneNode(true);
      const productsContainer = shell.querySelector("[data-products-container]");
      if (!productsContainer) return;

      productsContainer.replaceChildren(...selected);
      this.innerHTML = shell.innerHTML;
      this.products = selected.length;
      this.finishRender();
      this.subscribeToCartUpdates();
    }

    async fetchSection(url) {
      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`Recommendation request failed (${response.status})`);

      const text = await response.text();
      const documentFragment = new DOMParser().parseFromString(text, "text/html");
      return documentFragment.querySelector("m-recently-viewed");
    }

    parseList(value = "") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    scoreRecommendation(item) {
      const productModels = new Set(this.parseList(item.dataset.models));
      const modelMatch = [...productModels].some((model) => this.cartModels.has(model));
      const sourceScores = { complementary: 500, related: 280, recent: 80 };
      let score = sourceScores[item.dataset.recommendationSource] || 0;

      if (modelMatch) score += 800;
      else if (!productModels.size) score += 120;

      if (item.dataset.simpleVariant === "true") score += 90;

      const price = this.getPrice(item);
      if (price <= 3000) score += 160;
      else if (price <= 6000) score += 120;
      else if (price <= 10000) score += 80;
      else if (price <= 20000) score += 40;

      return score;
    }

    getPrice(item) {
      const price = parseInt(item.dataset.price, 10);
      return Number.isFinite(price) ? price : Number.MAX_SAFE_INTEGER;
    }

    finishRender() {
      this.classList.remove("m:hidden");
      this.initByScreenSize();

      if (!this.viewportEventsBound) {
        document.addEventListener("matchMobile", this.boundInitByScreenSize);
        document.addEventListener("unmatchMobile", this.boundInitByScreenSize);
        this.viewportEventsBound = true;
      }

      if (window.MinimogTheme?.CompareProduct?.setCompareButtonsState) {
        window.MinimogTheme.CompareProduct.setCompareButtonsState();
      }
      if (window.MinimogTheme?.Wishlist?.setWishlistButtonsState) {
        window.MinimogTheme.Wishlist.setWishlistButtonsState();
      }
    }

    subscribeToCartUpdates() {
      if (this.cartUpdateUnsubscriber || !window.MinimogEvents || !window.MinimogTheme?.pubSubEvents?.cartUpdate) return;

      this.cartUpdateUnsubscriber = window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        (event = {}) => {
          const productIds = new Set();
          if (event.product_id) productIds.add(String(event.product_id));
          if (Array.isArray(event.items)) {
            event.items.forEach((item) => {
              if (item.product_id) productIds.add(String(item.product_id));
            });
          }
          productIds.forEach((productId) => this.removeRecommendation(productId));
        }
      );
    }

    removeRecommendation(productId) {
      const items = [...this.querySelectorAll("[data-recommendation-item]")];
      const index = items.findIndex((item) => String(item.dataset.productId) === String(productId));
      if (index < 0) return;

      if (this.slider && !this.slider.destroyed && typeof this.slider.removeSlide === "function") {
        this.slider.removeSlide(index);
      } else {
        items[index].remove();
      }

      this.products = this.querySelectorAll("[data-recommendation-item]").length;
      if (!this.products) {
        this.classList.add("m:hidden");
        return;
      }

      const controls = this.querySelector(this.selectors.slideControls);
      if (controls && this.products <= this.items) controls.classList.add("m:hidden");
    }

    initByScreenSize() {
      if (!this.enableSlider) return;

      const { slideContainer, slideControls } = queryDomNodes(this.selectors, this);
      if (!slideContainer) return;

      if (window.MinimogTheme?.config?.mqlMobile && this.mobileDisableSlider) {
        slideControls?.classList.add("m:hidden");
        slideContainer.classList.remove("swiper-container");
        if (this.slider && !this.slider.destroyed) this.slider.destroy(false, true);
        this.slider = null;
        this.swiper = null;
      } else {
        slideControls?.classList.remove("m:hidden");
        this.initSlider();
      }

      if (this.products <= this.items) slideControls?.classList.add("m:hidden");
    }

    initSlider() {
      const { slideContainer } = queryDomNodes(this.selectors, this);
      const controlsContainer = this.querySelector(this.selectors.slideControls);
      const wrapper = slideContainer?.querySelector(".swiper-wrapper");
      if (!slideContainer || !wrapper) return;

      const existingSlider = slideContainer.swiper || this.slider;
      if (existingSlider && !existingSlider.destroyed) {
        this.slider = existingSlider;
        this.swiper = existingSlider;
        existingSlider.update();
        return;
      }

      const previousButton = controlsContainer?.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer?.querySelector(".m-slider-controls__button-next");
      const slideItemsLength = wrapper.childElementCount;
      slideContainer.classList.add("swiper-container");

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.products > this.items,
        pagination: this.showPagination
          ? { el: this.querySelector(".swiper-pagination"), clickable: true }
          : false,
        breakpoints: {
          768: { slidesPerView: this.items >= 3 ? 3 : this.items },
          1024: { slidesPerView: this.items >= 4 ? 4 : this.items },
          1280: { slidesPerView: this.items },
        },
        threshold: 2,
        on: {
          init: () => {
            setTimeout(() => {
              const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                controlsContainer.style.setProperty("--offset-top", `${parseInt(firstItem.clientHeight, 10) / 2}px`);
                previousButton?.classList.remove("m:hidden");
                nextButton?.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (!controlsContainer) return;
            if (slideItemsLength > breakpointParams.slidesPerView) {
              controlsContainer.classList.remove("m:hidden");
              swiper.allowTouchMove = true;
            } else {
              controlsContainer.classList.add("m:hidden");
              swiper.allowTouchMove = false;
            }
          },
        },
      });

      if (this.showNavigation) {
        previousButton?.addEventListener("click", () => this.slider?.slidePrev());
        nextButton?.addEventListener("click", () => this.slider?.slideNext());
      }
      this.swiper = slideContainer.swiper;
    }

    getQueryString(limit) {
      let items = [];
      try {
        items = JSON.parse(window.localStorage.getItem("minimog-recently-viewed") || "[]");
      } catch (error) {
        console.warn("Unable to read recently viewed products", error);
      }

      const currentProductId = this.dataset.productId ? String(this.dataset.productId) : "";
      const filteredItems = items
        .map((item) => String(item))
        .filter((item, index, allItems) => {
          return item && item !== currentProductId && !this.cartProductIds.has(item) && allItems.indexOf(item) === index;
        })
        .slice(0, limit);

      return encodeURIComponent(filteredItems.map((item) => `id:${item}`).join(" OR "));
    }
  }

  customElements.define("m-recently-viewed", MRecentlyViewed);
}
